import { forwardRef, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { CheckIcon, CloseIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { Spinner } from "@chakra-ui/react";
import { Interaction } from "../../../redux/reducers/types";
import { CaptionComponent } from "../CaptionComponent/CaptionComponent";
import { ProfileAvatar } from "../ProfileAvatar/ProfileAvatar";
import { ConfirmActionModal } from "../ConfirmActionModal/ConfirmActionModal";
import { CommentWrapper } from "./styles";
import {
  useDeleteCommentFromPostMutation,
  useUpdateCommentOnPostMutation,
} from "../../../redux/reducers/apiSlice";

interface Props {
  comment: Interaction;
  fromNotification?: boolean;
  postId: string;
  currentUserId?: string;
}

const formatTimestamp = (dateString?: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined });
};

export const Comment = forwardRef(
  ({ comment, fromNotification, postId, currentUserId }: Props, ref) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [actionsSupported, setActionsSupported] = useState(true);
    const [displayMessage, setDisplayMessage] = useState(comment.data?.message || "");
    const [editedMessage, setEditedMessage] = useState(comment.data?.message || "");
    const [updateComment, { isLoading: isUpdating }] =
      useUpdateCommentOnPostMutation();
    const [deleteComment, { isLoading: isDeleting }] =
      useDeleteCommentFromPostMutation();

    const isOwnComment = !!currentUserId && currentUserId === comment.ownerId;
    const isProcessing = isUpdating || isDeleting;

    useEffect(() => {
      const message = comment.data?.message || "";
      setDisplayMessage(message);
      setEditedMessage(message);
    }, [comment.id, comment.data?.message]);

    const isRouteNotAvailableError = (error: any) => {
      if (error?.status !== 404) return false;
      const message = error?.data?.message || error?.error || "";
      return message === "Not Found";
    };

    const getApiErrorMessage = (error: any, fallback: string) => {
      return error?.data?.message || error?.error || fallback;
    };

    const handleSaveEdit = async () => {
      const trimmedMessage = editedMessage.trim();
      if (!trimmedMessage) {
        toast.error("Comment cannot be empty");
        return;
      }

      try {
        await updateComment({
          postId,
          interactionId: comment.id,
          data: { message: trimmedMessage },
        }).unwrap();
        setDisplayMessage(trimmedMessage);
        setEditedMessage(trimmedMessage);
        setIsEditing(false);
      } catch (error: any) {
        if (isRouteNotAvailableError(error)) {
          setActionsSupported(false);
          setIsEditing(false);
          toast.error("Comment edit is not available in this environment yet.");
          return;
        }
        toast.error(getApiErrorMessage(error, "Unable to update comment. Please try again."));
      }
    };

    const handleDelete = async () => {
      try {
        await deleteComment({ postId, interactionId: comment.id }).unwrap();
        setIsDeleteModalOpen(false);
      } catch (error: any) {
        if (isRouteNotAvailableError(error)) {
          setActionsSupported(false);
          toast.error("Comment delete is not available in this environment yet.");
          return;
        }
        toast.error(getApiErrorMessage(error, "Unable to delete comment. Please try again."));
      }
    };

    return (
      // @ts-ignore
      <CommentWrapper $fromNotification={fromNotification} ref={ref}>
        <ProfileAvatar
          src={comment.ownerProfilePhoto}
          name={comment.ownerName}
        />
        <div className="name-and-comment-container">
          <div className="comment-header-row">
            <div style={{ display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
              <span className="name">{comment.ownerName}</span>
              {comment.createdAt && (
                <span className="comment-timestamp">
                  {formatTimestamp(comment.createdAt)}
                </span>
              )}
            </div>
            {isOwnComment && actionsSupported && (
              <div className="comment-actions">
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      className="action-btn icon-btn save"
                      onClick={handleSaveEdit}
                      disabled={isProcessing}
                      aria-label="Save comment"
                      title="Save"
                    >
                      {isUpdating ? <Spinner size="xs" /> : <CheckIcon boxSize={3.5} />}
                      <span>{isUpdating ? "Saving..." : "Save"}</span>
                    </button>
                    <button
                      type="button"
                      className="action-btn icon-btn cancel"
                      onClick={() => {
                        setEditedMessage(displayMessage);
                        setIsEditing(false);
                      }}
                      disabled={isProcessing}
                      aria-label="Cancel editing comment"
                      title="Cancel"
                    >
                      <CloseIcon boxSize={2.5} />
                      <span>Cancel</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="action-btn icon-btn edit"
                      onClick={() => setIsEditing(true)}
                      disabled={isProcessing}
                      aria-label="Edit comment"
                      title="Edit"
                    >
                      <EditIcon boxSize={3.5} />
                      <span>Edit</span>
                    </button>
                    <button
                      type="button"
                      className="action-btn icon-btn delete"
                      onClick={() => setIsDeleteModalOpen(true)}
                      disabled={isProcessing}
                      aria-label="Delete comment"
                      title="Delete"
                    >
                      {isDeleting ? <Spinner size="xs" /> : <DeleteIcon boxSize={3.5} />}
                      <span>{isDeleting ? "Deleting..." : "Delete"}</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          {isEditing ? (
            <textarea
              className="comment-edit-textarea"
              value={editedMessage}
              onChange={(event) => setEditedMessage(event.target.value)}
              maxLength={200}
              disabled={isProcessing}
            />
          ) : (
            <CaptionComponent caption={displayMessage} />
          )}
        </div>
        <ConfirmActionModal
            isOpen={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            onConfirm={handleDelete}
            isLoading={isDeleting}
            title="Delete comment?"
            message="This action cannot be undone."
            confirmText="Delete"
            cancelText="Cancel"
            cypressModalHeaderSelector="cypress-delete-comment-modal-header"
          />
      </CommentWrapper>
    );
  }
);
